/**
 * Assessment REST API Client
 * Interfaces with LoopBack 4 endpoints in fa-backend, with offline fallback caching
 * and built-in sample tests/questions with image option support.
 */
import { AssessmentQuestion, QuestionType } from '../types/Question';
import { AssessmentTest, AssignedTest, TestGroup } from '../types/Test';
import { AssessmentAssignment, AssignedAssignment, AIAssessmentFeedback } from '../types/Assignment';
import { AuthService } from '../../services/AuthService';

export class AssessmentApiService {
  private static instance: AssessmentApiService;
  private baseUrl: string;

  public static getInstance(): AssessmentApiService {
    if (!AssessmentApiService.instance) {
      AssessmentApiService.instance = new AssessmentApiService();
    }
    return AssessmentApiService.instance;
  }

  private constructor() {
    const isTestEnv = typeof process !== 'undefined' && (process.env?.VITEST || process.env?.NODE_ENV === 'test');
    this.baseUrl = (isTestEnv || typeof window === 'undefined' || !window.location?.protocol?.startsWith('http'))
      ? 'http://localhost:3000/api'
      : '/api';
    this.initDefaultMockData();
  }

  private getHeaders(): Record<string, string> {
    const authHeaders = AuthService.getInstance().getAuthHeaders();
    return {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...authHeaders
    };
  }

  private getAuthHeaders(): HeadersInit {
    return this.getHeaders();
  }

  private async apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = { ...this.getHeaders(), ...(options.headers as Record<string, string> || {}) };
    let response = await fetch(url, {
      ...options,
      headers
    });

    if (response.status === 401) {
      const errClone = response.clone();
      const errText = await errClone.text().catch(() => '');

      if (errText.includes('jwt expired') || errText.includes('Unauthorized') || errText.includes('Error verifying token')) {
        console.warn('[AssessmentApiService] JWT expired (401). Attempting automatic refresh via /api/users/refresh...');
        const newToken = await AuthService.getInstance().refreshToken();
        if (newToken) {
          const retryHeaders = {
            ...headers,
            'Authorization': `Bearer ${newToken}`
          };
          response = await fetch(url, {
            ...options,
            headers: retryHeaders
          });
        }
      }
    }

    return response;
  }

  // ==========================================
  // TESTS & EXAMS API
  // ==========================================

  public async getTests(courseId?: string): Promise<AssessmentTest[]> {
    const local = this.getLocalTests();
    try {
      const filterObj = courseId ? { where: { courseId } } : {};
      const filter = Object.keys(filterObj).length ? `?filter=${encodeURIComponent(JSON.stringify(filterObj))}` : '';
      const res = await this.apiFetch(`${this.baseUrl}/test-tests${filter}`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          const seen = new Set(data.map((d: any) => d.id));
          const uniqueLocal = local.filter(l => !seen.has(l.id));
          return [...uniqueLocal, ...data];
        }
      }
    } catch {
      // Fallback to local store
    }
    return local;
  }

  public async getTestById(testId: string): Promise<AssessmentTest | null> {
    try {
      const res = await this.apiFetch(`${this.baseUrl}/test-tests/${testId}`);
      if (res.ok) return await res.json();
    } catch {
      // Fallback
    }
    return this.getLocalTests().find(t => t.id === testId) || null;
  }

  public async getTestGroupById(groupId: string): Promise<TestGroup | null> {
    if (!groupId) return null;
    try {
      const res = await this.apiFetch(`${this.baseUrl}/test-groups/${groupId}`);
      if (res.ok) return await res.json();
    } catch {
      // Fallback
    }
    return null;
  }

  public async createTest(test: AssessmentTest): Promise<AssessmentTest> {
    const auth = AuthService.getInstance();
    const userId = (test as any).userId || auth.getUserId() || undefined;
    const companyId = (test as any).companyId || auth.getCompanyId() || '663dbe102a581c62fda06806';

    const payload: any = {
      name: test.name,
      courseId: test.courseId,
      duration: test.duration ?? 15,
      level: test.level || 'Beginner',
      topic: test.topic || 'General',
      subject: test.subject || 'General',
      category: test.category || 'Theory',
      language: test.language || 'en',
      points: test.points ?? 100,
      minPointsToPass: test.minPointsToPass ?? 60,
      questionsCount: test.questions?.length ?? test.questionsCount ?? 0,
      questions: test.questions || [],
      retakeCount: test.retakeCount ?? 3,
      hintMinusPoint: test.hintMinusPoint ?? 0,
      userId,
      companyId,
      config: test.config || {}
    };

    try {
      const res = await fetch(`${this.baseUrl}/test-tests`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const saved = await res.json();
        const local = this.getLocalTests();
        local.unshift(saved);
        this.saveLocalTests(local);
        return saved;
      }
    } catch {
      // Fallback
    }

    const local = this.getLocalTests();
    const created: AssessmentTest = {
      ...payload,
      id: test.id || `test-${Date.now()}`
    };
    local.unshift(created);
    this.saveLocalTests(local);
    return created;
  }

  public async updateTest(id: string, test: Partial<AssessmentTest>): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/test-tests/${id}`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(test)
      });
      if (res.ok) {
        const local = this.getLocalTests();
        const idx = local.findIndex(t => t.id === id);
        if (idx !== -1) {
          local[idx] = { ...local[idx], ...test };
          this.saveLocalTests(local);
        }
        return true;
      }
    } catch (err) {
      console.error('[AssessmentApiService] updateTest error:', err);
    }
    const local = this.getLocalTests();
    const idx = local.findIndex(t => t.id === id);
    if (idx !== -1) {
      local[idx] = { ...local[idx], ...test };
      this.saveLocalTests(local);
      return true;
    }
    return false;
  }

  public async deleteTest(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/test-tests/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      if (res.ok) {
        const local = this.getLocalTests().filter(t => t.id !== id);
        this.saveLocalTests(local);
        return true;
      }
    } catch (err) {
      console.error('[AssessmentApiService] deleteTest error:', err);
    }
    const local = this.getLocalTests().filter(t => t.id !== id);
    this.saveLocalTests(local);
    return true;
  }

  // ==========================================
  // TEST GROUPS API
  // ==========================================

  public async getTestGroups(): Promise<TestGroup[]> {
    try {
      const filter = encodeURIComponent(JSON.stringify({ order: ['created DESC'] }));
      const res = await fetch(`${this.baseUrl}/test-groups?filter=${filter}`, {
        headers: this.getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) return data;
      }
    } catch (err) {
      console.error('[AssessmentApiService] getTestGroups error:', err);
    }
    const local = localStorage.getItem('asae_test_groups');
    if (local) {
      try {
        const parsed = JSON.parse(local);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch {}
    }
    return [];
  }

  public async createTestGroup(group: Partial<TestGroup>): Promise<TestGroup> {
    const auth = AuthService.getInstance();
    const userId = (group as any).userId || auth.getUserId() || undefined;
    const companyId = (group as any).companyId || auth.getCompanyId() || '663dbe102a581c62fda06806';

    const payload: Partial<TestGroup> = {
      name: group.name || 'New Test Group',
      description: group.description || '',
      category: group.category || 'Theory',
      complexity: group.complexity || 'Mix/AverageComplexity',
      userId,
      companyId,
      config: group.config || {}
    };

    try {
      const res = await fetch(`${this.baseUrl}/test-groups`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const created = await res.json();
        const local = await this.getTestGroups();
        local.unshift(created);
        localStorage.setItem('asae_test_groups', JSON.stringify(local));
        return created;
      }
    } catch (err) {
      console.error('[AssessmentApiService] createTestGroup error:', err);
    }

    const created: TestGroup = {
      ...payload,
      id: `tg-${Date.now()}`
    } as TestGroup;
    const local = await this.getTestGroups();
    local.unshift(created);
    localStorage.setItem('asae_test_groups', JSON.stringify(local));
    return created;
  }

  public async updateTestGroup(id: string, group: Partial<TestGroup>): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/test-groups/${id}`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(group)
      });
      if (res.ok) {
        const local = await this.getTestGroups();
        const idx = local.findIndex(g => g.id === id);
        if (idx !== -1) {
          local[idx] = { ...local[idx], ...group };
          localStorage.setItem('asae_test_groups', JSON.stringify(local));
        }
        return true;
      }
    } catch (err) {
      console.error('[AssessmentApiService] updateTestGroup error:', err);
    }
    const local = await this.getTestGroups();
    const idx = local.findIndex(g => g.id === id);
    if (idx !== -1) {
      local[idx] = { ...local[idx], ...group };
      localStorage.setItem('asae_test_groups', JSON.stringify(local));
      return true;
    }
    return false;
  }

  // ==========================================
  // QUESTIONS BANK API
  // ==========================================

  public async getQuestions(topicOrOptions?: string | {
    topic?: string;
    subject?: string;
    level?: string;
    coursePageId?: string;
    pageId?: string;
    limit?: number;
    skip?: number;
  }, subjectParam?: string): Promise<AssessmentQuestion[]> {
    const local = this.getLocalQuestions();
    try {
      const where: any = {};
      let limit = 100;
      let skip = 0;

      if (typeof topicOrOptions === 'object' && topicOrOptions !== null) {
        if (topicOrOptions.topic && topicOrOptions.topic !== 'ALL') where.topic = topicOrOptions.topic;
        if (topicOrOptions.subject && topicOrOptions.subject !== 'ALL') where.subject = topicOrOptions.subject;
        if (topicOrOptions.level && topicOrOptions.level !== 'ALL') where.level = topicOrOptions.level;
        const pageTarget = topicOrOptions.coursePageId || topicOrOptions.pageId;
        if (pageTarget && pageTarget !== 'ALL') {
          where.or = [{ pageId: pageTarget }, { coursePageId: pageTarget }];
        }
        if (topicOrOptions.limit) limit = topicOrOptions.limit;
        if (topicOrOptions.skip) skip = topicOrOptions.skip;
      } else {
        if (topicOrOptions && topicOrOptions !== 'ALL') where.topic = topicOrOptions;
        if (subjectParam && subjectParam !== 'ALL') where.subject = subjectParam;
      }

      const filterObj: any = { limit, skip, order: ['created DESC'] };
      if (Object.keys(where).length > 0) filterObj.where = where;
      const filter = `?filter=${encodeURIComponent(JSON.stringify(filterObj))}`;
      const res = await this.apiFetch(`${this.baseUrl}/test-questions${filter}`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          const seen = new Set(data.map((d: any) => d.id));
          const uniqueLocal = local.filter(l => !seen.has(l.id));
          return [...uniqueLocal, ...data];
        }
      }
    } catch {
      // Fallback
    }
    return local;
  }

  public async getQuestionsByIds(ids: Array<string | any>, topic?: string, subject?: string): Promise<AssessmentQuestion[]> {
    if (!ids || ids.length === 0) return [];

    const cleanIds = ids.map(id => typeof id === 'object' && id !== null ? (id.id || id._id) : id).filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
    if (cleanIds.length === 0) return [];

    const fetchedMap = new Map<string, AssessmentQuestion>();

    // Query in parallel batches of 10 via findById endpoint (/api/test-questions/{id})
    // which reliably casts MongoDB ObjectId on LoopBack 4.
    const batchSize = 10;
    for (let i = 0; i < cleanIds.length; i += batchSize) {
      const batch = cleanIds.slice(i, i + batchSize);
      await Promise.all(batch.map(async (id) => {
        try {
          const res = await this.apiFetch(`${this.baseUrl}/test-questions/${encodeURIComponent(id)}`);
          if (res.ok) {
            const q = await res.json();
            if (q && (q.id || q._id)) {
              fetchedMap.set(q.id || q._id, q);
            }
          }
        } catch (err) {
          console.warn(`[AssessmentApiService] Failed to fetch question ${id}:`, err);
        }
      }));
    }

    // If any questions were not found via API, check local questions
    const missingIds = cleanIds.filter(id => !fetchedMap.has(id));
    if (missingIds.length > 0) {
      const local = this.getLocalQuestions();
      local.forEach((q: AssessmentQuestion) => {
        const qId = q.id || (q as any)._id;
        if (qId && missingIds.includes(qId) && !fetchedMap.has(qId)) {
          fetchedMap.set(qId, q);
        }
      });
    }

    const result: AssessmentQuestion[] = [];
    cleanIds.forEach(id => {
      const q = fetchedMap.get(id);
      if (q) result.push(q);
    });

    if (result.length === 0 && fetchedMap.size > 0) {
      return Array.from(fetchedMap.values());
    }

    return result;
  }

  public async ensureQuestionsSaved(questions: AssessmentQuestion[]): Promise<string[]> {
    const questionIds: string[] = [];
    for (const q of questions) {
      if (q.id && !q.id.startsWith('q-') && !q.id.startsWith('temp-') && q.id.length >= 10) {
        questionIds.push(q.id);
      } else {
        try {
          const created = await this.createQuestion(q);
          if (created && created.id) {
            q.id = created.id;
            questionIds.push(created.id);
          }
        } catch (err) {
          console.error('[AssessmentApiService] Failed to save new question:', err);
          if (q.id) questionIds.push(q.id);
        }
      }
    }
    return questionIds;
  }

  public async createQuestion(question: AssessmentQuestion): Promise<AssessmentQuestion> {
    const auth = AuthService.getInstance();
    const userId = (question as any).userId || auth.getUserId() || undefined;
    const companyId = (question as any).companyId || auth.getCompanyId() || '663dbe102a581c62fda06806';

    const payload: any = {
      ...question,
      question: question.question,
      topic: question.topic || 'General',
      subject: question.subject || 'General',
      userId: (question as any).userId || userId,
      companyId: (question as any).companyId || companyId,
      pageId: (question as any).pageId || 'global-question-page',
      category: question.category || 'Theory',
      type: question.type || 'MultipleChoice'
    };

    try {
      const res = await fetch(`${this.baseUrl}/test-questions`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const saved = await res.json();
        const local = this.getLocalQuestions();
        local.unshift(saved);
        this.saveLocalQuestions(local);
        return saved;
      }
    } catch {
      // Fallback
    }
    const local = this.getLocalQuestions();
    const created = { ...payload, id: question.id || `q-${Date.now()}` };
    local.unshift(created);
    this.saveLocalQuestions(local);
    return created;
  }

  public async createMultipleQuestions(questions: AssessmentQuestion[]): Promise<AssessmentQuestion[]> {
    const auth = AuthService.getInstance();
    const userId = auth.getUserId() || undefined;
    const companyId = auth.getCompanyId() || '663dbe102a581c62fda06806';

    const formatted = questions.map(q => ({
      ...q,
      question: q.question,
      topic: q.topic || 'General',
      subject: q.subject || 'General',
      userId: (q as any).userId || userId,
      companyId: (q as any).companyId || companyId,
      pageId: (q as any).pageId || 'global-question-page',
      category: q.category || 'Theory',
      type: q.type || 'MultipleChoice'
    }));

    try {
      const res = await fetch(`${this.baseUrl}/test-questions/multiple`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(formatted)
      });
      if (res.ok) {
        const saved = await res.json();
        if (Array.isArray(saved)) {
          const local = this.getLocalQuestions();
          local.unshift(...saved);
          this.saveLocalQuestions(local);
          return saved;
        }
      }
    } catch {
      // Fallback
    }
    const local = this.getLocalQuestions();
    const created = formatted.map(q => ({ ...q, id: q.id || `q-${Date.now()}-${Math.random().toString(36).substr(2, 5)}` }));
    local.unshift(...created);
    this.saveLocalQuestions(local);
    return created;
  }

  // ==========================================
  // AI GENERATION API (prompts/get-content)
  // ==========================================

  /** Build a schema-hint sample per question type so the AI knows exactly what JSON to return */
  private buildQuestionSample(type: string): any {
    switch (type) {
      case 'MultipleChoice':
        return {
          question: "Sample question text here?",
          type: "MultipleChoice",
          options: [
            { value: "Option A text", isCorrect: false },
            { value: "Option B text", isCorrect: true },
            { value: "Option C text", isCorrect: false },
            { value: "Option D text", isCorrect: false }
          ],
          answer: { value: "Option B text" },
          explanation: "Brief explanation of why B is correct."
        };
      case 'MultipleResponse':
        return {
          question: "Select ALL that apply: Sample question?",
          type: "MultipleResponse",
          options: [
            { value: "Option A", isCorrect: true },
            { value: "Option B", isCorrect: false },
            { value: "Option C", isCorrect: true },
            { value: "Option D", isCorrect: false }
          ],
          answer: { value: ["Option A", "Option C"] },
          explanation: "Both A and C are correct because..."
        };
      case 'Matching':
        return {
          question: "Match each item on the left with its correct pair on the right.",
          type: "Matching",
          options: [
            { value: "Left Term 1", matchTarget: "Right Match 1" },
            { value: "Left Term 2", matchTarget: "Right Match 2" },
            { value: "Left Term 3", matchTarget: "Right Match 3" },
            { value: "Left Term 4", matchTarget: "Right Match 4" }
          ],
          answer: {
            pairs: [
              { left: "Left Term 1", right: "Right Match 1" },
              { left: "Left Term 2", right: "Right Match 2" },
              { left: "Left Term 3", right: "Right Match 3" },
              { left: "Left Term 4", right: "Right Match 4" }
            ]
          },
          explanation: "Brief explanation of the matching logic."
        };
      case 'TrueFalse':
        return {
          question: "A declarative statement that is either true or false.",
          type: "TrueFalse",
          answer: { value: "True" },
          explanation: "Explanation of why this statement is True."
        };
      case 'FillInBlanks':
        return {
          question: "The capital of France is ___.",
          type: "FillInBlanks",
          answer: { value: "Paris" },
          explanation: "Paris is the capital and largest city of France."
        };
      case 'ShortAnswer':
        return {
          question: "Briefly explain what photosynthesis is.",
          type: "ShortAnswer",
          answer: { value: "Photosynthesis is the process by which plants convert light into energy." },
          explanation: "Short answer should mention light, chlorophyll, glucose."
        };
      default:
        return {
          question: "Question text?",
          type,
          options: [{ value: "Option A", isCorrect: true }, { value: "Option B", isCorrect: false }],
          answer: { value: "Option A" }
        };
    }
  }

  public async generateQuestionsWithAI(payload: {
    subject: string;
    topic: string;
    type: string | string[];
    complexity?: number;
    level?: string;
    coursePageId?: string;
    correctPoints?: number;
    incorrectPoints?: number;
    hasCode?: boolean;
    codeLanguage?: string;
    correctFeedback?: string;
    incorrectFeedback?: string;
    noOfQuestions: number;
    content?: string;
    questionSample?: any;
  }): Promise<AssessmentQuestion[]> {
    const typesArray = Array.isArray(payload.type) ? payload.type : [payload.type];
    const typeString = typesArray.join(', ');

    // Build a per-type schema sample so the AI knows exactly what JSON structure to return
    let questionSample: any;
    if (payload.questionSample && Object.keys(payload.questionSample).length > 0) {
      questionSample = payload.questionSample;
    } else if (typesArray.length > 1) {
      questionSample = typesArray.map(t => this.buildQuestionSample(t));
    } else {
      questionSample = this.buildQuestionSample(typesArray[0] || 'MultipleChoice');
    }

    const modelPayload = {
      ...payload,
      type: typeString,
      questionSample
    };

    const body = {
      relatedTo: "test",
      appliedTo: "test-questions",
      model: modelPayload
    };

    try {
      const res = await fetch(`${this.baseUrl}/prompts/get-content`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(body)
      });

      if (res.ok) {
        let data = await res.json();
        if (typeof data === 'string') {
          const cleanJson = data.replace(/^```json\s*/i, '').replace(/\s*```$/, '').trim();
          try { data = JSON.parse(cleanJson); } catch {}
        }

        if (Array.isArray(data)) {
          return data.map((q, idx) => this.normalizeGeneratedQuestion(q, payload, idx));
        }

        if (data && typeof data === 'object') {
          if (Array.isArray(data.questions)) {
            return data.questions.map((q: any, idx: number) => this.normalizeGeneratedQuestion(q, payload, idx));
          }
          return [this.normalizeGeneratedQuestion(data, payload, 0)];
        }
      }
    } catch (err) {
      console.warn('[AssessmentApiService] AI question generation API error, using offline synthesis fallback:', err);
    }

    // Heuristic fallback for offline / test environments
    const count = payload.noOfQuestions || 3;
    const fallbackQuestions: AssessmentQuestion[] = [];
    const types = Array.isArray(payload.type) ? payload.type : [payload.type || 'MultipleChoice'];
    for (let i = 0; i < count; i++) {
      const qType = (types[i % types.length] || 'MultipleChoice') as QuestionType;
      const promptText = `Synthesized assessment question on ${payload.topic || 'General Science'} (#${i + 1}): Key characteristics and foundational principles.`;
      fallbackQuestions.push({
        id: `q-gen-${Date.now()}-${i}`,
        type: qType,
        question: promptText,
        questionText: promptText,
        text: promptText,
        subject: payload.subject || 'Science',
        topic: payload.topic || 'General',
        level: payload.level || 'Beginner',
        complexity: payload.complexity || 2,
        points: payload.correctPoints || 1,
        correctPoints: payload.correctPoints || 1,
        incorrectPoints: payload.incorrectPoints || 0,
        options: [
          { value: `Primary feature of ${payload.topic || 'the concept'}`, isCorrect: true },
          { value: `Secondary related factor`, isCorrect: false },
          { value: `Alternative unrelated principle`, isCorrect: false },
          { value: `Opposite conflicting state`, isCorrect: false }
        ],
        explanation: `This explains the foundational principles regarding ${payload.topic || 'the subject'}.`,
        description: `Focus on the core definition of ${payload.topic || 'the subject'}.`,
        code: payload.hasCode ? `function check() {\n  return true;\n}` : undefined,
        codeLanguage: payload.codeLanguage || 'javascript'
      });
    }
    return fallbackQuestions;
  }

  /**
   * Generates comprehensive practical assignment instructions and rubrics via AI.
   */
  public async generateAssignmentInstructionsWithAI(params: {
    name: string;
    subject: string;
    topic: string;
    level: string;
    submissionType?: string;
  }): Promise<string> {
    try {
      const body = {
        relatedTo: 'assignment',
        appliedTo: 'instructions',
        model: {
          topic: params.topic || params.name,
          subject: params.subject,
          level: params.level,
          content: params.name,
          instructions: `Synthesize detailed practical assignment instructions for "${params.name}" under subject "${params.subject}" and topic "${params.topic}".`
        }
      };

      const res = await fetch(`${this.baseUrl}/prompts/get-content`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(body)
      });

      if (res.ok) {
        let data = await res.json();
        let htmlContent = '';
        if (typeof data === 'string') {
          htmlContent = data.replace(/^```html\s*/i, '').replace(/\s*```$/, '').trim();
        } else if (data && typeof data === 'object') {
          htmlContent = data.instructions || data.content || data.text || '';
        }
        if (htmlContent && htmlContent.trim().length > 30) {
          return htmlContent;
        }
      }
    } catch (err) {
      console.warn('[AssessmentApiService] AI assignment instructions generation error:', err);
    }

    return `<p><strong>Assignment Goal:</strong> Master practical execution for ${params.name} in ${params.subject}.</p>
<p><strong>Assignment Instructions:</strong></p>
<ol>
  <li>Set up your development workspace according to ${params.topic} requirements.</li>
  <li>Implement core solution logic demonstrating clean coding standards and modular design.</li>
  <li>Perform testing and document your findings.</li>
</ol>
<p><strong>Evaluation Criteria:</strong></p>
<ul>
  <li>Code & Structure Quality (40%)</li>
  <li>Functionality & Output (40%)</li>
  <li>Documentation & Cleanliness (20%)</li>
</ul>`;
  }

  /**
   * Delimiters the AI commonly uses to combine left + right matching terms into one string.
   * e.g. "Mercury - Inner planet", "Mercury → Inner planet", "Mercury | Inner planet"
   */
  private readonly MATCH_DELIMITERS = [' → ', ' -> ', ' | ', ' : ', ' - '];

  /**
   * Try to split a combined matching string into {left, right} using known delimiters.
   * Returns null if no delimiter found.
   */
  private splitMatchingString(s: string): { left: string; right: string } | null {
    for (const delim of this.MATCH_DELIMITERS) {
      const idx = s.indexOf(delim);
      if (idx > 0) {
        return { left: s.substring(0, idx).trim(), right: s.substring(idx + delim.length).trim() };
      }
    }
    return null;
  }

  /**
   * Normalize a single option item from any AI output shape into {value, isCorrect, matchTarget?}
   * Handles: plain string, {text, isCorrect}, {label, correct}, {option, answer}, {left, right}, etc.
   * Pass isMatching=true for Matching questions so combined strings get split.
   */
  private normalizeOption(
    o: any,
    correctAnswer?: string,
    isMatching: boolean = false
  ): { value: string; isCorrect: boolean; matchTarget?: string; image?: string } {
    // Plain string option
    if (typeof o === 'string') {
      if (isMatching) {
        const split = this.splitMatchingString(o);
        if (split) return { value: split.left, isCorrect: false, matchTarget: split.right };
      }
      const isCorrect = correctAnswer !== undefined
        ? (Array.isArray(correctAnswer) ? (correctAnswer as any[]).includes(o) : correctAnswer === o)
        : false;
      return { value: o, isCorrect };
    }

    // Matching pair: {left, right} or {term, definition} or {key, match} etc.
    if (o.left !== undefined || o.term !== undefined || o.key !== undefined) {
      return {
        value: o.left || o.term || o.key || '',
        isCorrect: false,
        matchTarget: o.right || o.definition || o.match || o.matchTarget || ''
      };
    }

    // Standard object — extract value from various field names
    const rawVal =
      o.value !== undefined ? o.value :
      o.text  !== undefined ? o.text  :
      o.label !== undefined ? o.label :
      o.option !== undefined ? o.option :
      o.content !== undefined ? o.content :
      o.name  !== undefined ? o.name  :
      JSON.stringify(o);

    const textValue = String(rawVal);

    // For matching: if matchTarget is not set but value looks like "left - right", split it
    let matchTarget: string | undefined = o.matchTarget || o.match || o.pair || o.mapping || undefined;
    if (isMatching && !matchTarget && textValue) {
      const split = this.splitMatchingString(textValue);
      if (split) {
        return {
          value: split.left,
          isCorrect: false,
          matchTarget: split.right,
          image: o.image || o.imageUrl || undefined
        };
      }
    }

    // Extract correctness from various field names
    const isCorrect: boolean =
      o.isCorrect !== undefined ? Boolean(o.isCorrect) :
      o.correct   !== undefined ? Boolean(o.correct)   :
      o.is_correct !== undefined ? Boolean(o.is_correct) :
      o.answer    !== undefined ? Boolean(o.answer)    :
      (correctAnswer !== undefined
        ? (Array.isArray(correctAnswer) ? (correctAnswer as any[]).includes(textValue) : correctAnswer === textValue)
        : false);

    return {
      value: textValue,
      isCorrect,
      matchTarget,
      image: o.image || o.imageUrl || undefined
    };
  }

  private normalizeGeneratedQuestion(q: any, payload: any, idx: number): AssessmentQuestion {
    const qType = (q.type || payload.type || 'MultipleChoice') as QuestionType;
    const correctPts = payload.correctPoints ?? q.correctPoints ?? 1;
    const incorrectPts = payload.incorrectPoints ?? q.incorrectPoints ?? 0;

    // Normalize options — handle every possible AI output shape
    let normalizedOptions: { value: string; isCorrect: boolean; matchTarget?: string; image?: string }[];
    const rawOptions = q.options || q.choices || q.answers || q.variants;
    const isMatching = qType === 'Matching';

    if (isMatching) {
      // ── MATCHING: multiple possible AI output shapes ─────────────────────────────────
      //
      // Shape 1: options=[{value,matchTarget}] — ideal, already correct
      // Shape 2: options=[{left,right}]        — handled by normalizeOption
      // Shape 3: options=["Left - Right"]       — combined string, needs split
      // Shape 4: options=["Left"], matchingOptions=["Right"] — zip required
      // Shape 5: answer.pairs=[{left,right}]   — reconstruct from answer
      // Shape 6: answer.value=[{option,matchingOption}] — reconstruct from answer

      const rawMatchingOptions: any[] = q.matchingOptions || q.targets || q.matches || [];

      if (Array.isArray(rawOptions) && rawOptions.length > 0 && rawMatchingOptions.length === rawOptions.length) {
        // Shape 4: zip options + matchingOptions
        normalizedOptions = rawOptions.map((o: any, i: number) => ({
          value: typeof o === 'string' ? o : String(o.value || o.text || o.label || o),
          isCorrect: false,
          matchTarget: typeof rawMatchingOptions[i] === 'string'
            ? rawMatchingOptions[i]
            : String(rawMatchingOptions[i]?.value || rawMatchingOptions[i]?.text || rawMatchingOptions[i] || '')
        }));
      } else if (Array.isArray(rawOptions) && rawOptions.length > 0) {
        // Shape 1-3: normalizeOption with isMatching=true handles split
        normalizedOptions = rawOptions.map((o: any) => this.normalizeOption(o, undefined, true));
      } else {
        // Shapes 5 & 6: reconstruct from answer field
        const rawPairs: any[] =
          (q.answer as any)?.pairs ||
          (q.answer as any)?.matchingPairs ||
          (Array.isArray((q.answer as any)?.value) ? (q.answer as any).value : []);

        if (rawPairs.length > 0) {
          normalizedOptions = rawPairs.map((p: any) => ({
            value: p.left || p.option || p.term || p.key || String(p),
            isCorrect: false,
            matchTarget: p.right || p.matchingOption || p.definition || p.match || ''
          }));
        } else {
          // Absolute fallback placeholders
          normalizedOptions = [
            { value: 'Item 1', isCorrect: false, matchTarget: 'Match 1' },
            { value: 'Item 2', isCorrect: false, matchTarget: 'Match 2' }
          ];
        }
      }

      // Post-process: if any option still has no matchTarget, try to split the value itself
      normalizedOptions = normalizedOptions.map(o => {
        if (!o.matchTarget || o.matchTarget.trim() === '') {
          const split = this.splitMatchingString(o.value);
          if (split) return { ...o, value: split.left, matchTarget: split.right };
        }
        return o;
      });

    } else if (qType === 'TrueFalse') {
      // ── TRUE/FALSE: robust case-insensitive detection ────────────────────────────────
      const rawAns = q.answer?.value ?? q.answer ?? q.correctAnswer ?? '';
      const ansStr = String(rawAns).toLowerCase().trim();
      const isTrue = ansStr === 'true' || ansStr === '1' || ansStr === 'yes';
      normalizedOptions = [
        { value: 'True', isCorrect: isTrue },
        { value: 'False', isCorrect: !isTrue }
      ];

    } else if (qType === 'FillInBlanks' || qType === 'ShortAnswer' || qType === 'LongAnswer') {
      // ── FILL IN BLANKS / SHORT ANSWER: answer may be string or array ─────────────────
      const rawAns = q.answer?.value ?? q.answer ?? q.correctAnswer ?? '';
      const ansStr = Array.isArray(rawAns) ? rawAns.join(' / ') : String(rawAns);
      normalizedOptions = [{ value: ansStr, isCorrect: true }];

    } else if (Array.isArray(rawOptions) && rawOptions.length > 0) {
      // ── MCQ / MULTI-RESPONSE: standard option list ───────────────────────────────────
      const correctHint =
        typeof q.answer?.value === 'string' ? q.answer.value :
        q.correct_answer ?? q.correctAnswer ?? undefined;
      normalizedOptions = rawOptions.map((o: any) =>
        this.normalizeOption(o, typeof correctHint === 'string' ? correctHint : undefined, false)
      );

    } else {
      // Fallback placeholder options
      normalizedOptions = [
        { value: 'Option A', isCorrect: true },
        { value: 'Option B', isCorrect: false },
        { value: 'Option C', isCorrect: false },
        { value: 'Option D', isCorrect: false }
      ];
    }

    // ── NORMALIZE ANSWER FIELD ───────────────────────────────────────────────────────
    let normalizedAnswer: any = q.answer;
    if (normalizedAnswer === undefined || normalizedAnswer === null) {
      if (isMatching) {
        normalizedAnswer = {
          pairs: normalizedOptions.map(o => ({ left: o.value, right: o.matchTarget || '' }))
        };
      } else {
        const firstCorrect = normalizedOptions.find(o => o.isCorrect);
        normalizedAnswer = firstCorrect ? { value: firstCorrect.value } : { value: '' };
      }
    } else if (typeof normalizedAnswer === 'string') {
      normalizedAnswer = { value: normalizedAnswer };
    } else if (isMatching && !normalizedAnswer.pairs) {
      // Ensure matching answer has pairs
      normalizedAnswer = {
        ...normalizedAnswer,
        pairs: normalizedOptions.map(o => ({ left: o.value, right: o.matchTarget || '' }))
      };
    }

    return {
      id: q.id || `q-ai-${Date.now()}-${idx}`,
      question: q.question || q.text || q.stem || q.prompt || `${payload.subject} - ${payload.topic} Question ${idx + 1}`,
      type: qType,
      subject: q.subject || payload.subject || 'General',
      topic: q.topic || payload.topic || 'General',
      level: q.level || payload.level || String(payload.complexity || 2),
      coursePageId: q.coursePageId || payload.coursePageId || undefined,
      pageId: q.pageId || q.coursePageId || payload.coursePageId || undefined,
      complexity: q.complexity || payload.complexity || 2,
      correctPoints: correctPts,
      incorrectPoints: incorrectPts,
      image: q.image || q.imageUrl || undefined,
      code: q.code || q.codeSnippet || undefined,
      codeLanguage: q.codeLanguage || payload.codeLanguage || 'javascript',
      options: normalizedOptions,
      answer: normalizedAnswer,
      explanation: q.explanation || q.rationale || q.reason || undefined,
      description: q.description || q.hint || q.note || undefined,
      feedback: (payload.correctFeedback || payload.incorrectFeedback || q.feedback) ? {
        correct: payload.correctFeedback || q.feedback?.correct || undefined,
        incorrect: payload.incorrectFeedback || q.feedback?.incorrect || undefined
      } : undefined
    };
  }

  // ==========================================
  // ASSIGNED TESTS (LEARNER ATTEMPTS)
  // ==========================================

  public async getAssignedTests(candidateId?: string): Promise<AssignedTest[]> {
    const auth = AuthService.getInstance();
    const currentUserId = auth.getUserId();
    const isPrivileged = auth.canCheckAssignments();

    // Students strictly query only their own attempts; instructors can pass a specific candidateId
    const targetCandidateId = (!isPrivileged) ? (currentUserId || candidateId || '') : (candidateId || '');
    if (!targetCandidateId && !isPrivileged) {
      return [];
    }

    try {
      const filterObj = targetCandidateId ? { where: { candidateId: targetCandidateId } } : {};
      const filter = Object.keys(filterObj).length > 0 ? `?filter=${encodeURIComponent(JSON.stringify(filterObj))}` : '';
      const res = await fetch(`${this.baseUrl}/test-assigned-tests${filter}`, {
        headers: this.getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) return data;
      }
    } catch {
      // Fallback
    }
    const local = this.getLocalAssignedTests(targetCandidateId);
    return targetCandidateId ? local.filter(t => t.candidateId === targetCandidateId) : (isPrivileged ? local : []);
  }

  public async startAssignedTest(payload: {
    testId: string;
    candidateId: string;
    questions: AssessmentQuestion[];
  }): Promise<AssignedTest> {
    const newAttempt: AssignedTest = {
      id: `attempt-${Date.now()}`,
      testId: payload.testId,
      candidateId: payload.candidateId,
      start: new Date().toISOString(),
      testStatus: 'Inprogress',
      questions: payload.questions,
      obtainedPoints: 0,
      totalTimeTaken: 0,
      totalCorrectAnswers: 0,
      totalIncorrectAnswers: 0,
      performedEvents: []
    };

    try {
      const res = await fetch(`${this.baseUrl}/test-assigned-tests`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(newAttempt)
      });
      if (res.ok) return await res.json();
    } catch {
      // Fallback
    }

    const local = this.getLocalAssignedTests(payload.candidateId);
    local.push(newAttempt);
    this.saveLocalAssignedTests(local, payload.candidateId);
    return newAttempt;
  }

  public async submitAssignedTest(assignedTestId: string, payload: Partial<AssignedTest>): Promise<AssignedTest> {
    try {
      const res = await fetch(`${this.baseUrl}/test-assigned-tests/${assignedTestId}`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (res.ok) return await res.json();
    } catch {
      // Fallback
    }

    const candidateId = payload.candidateId || AuthService.getInstance().getUserId() || undefined;
    const local = this.getLocalAssignedTests(candidateId);
    const index = local.findIndex(a => a.id === assignedTestId);
    if (index !== -1) {
      local[index] = { ...local[index], ...payload };
      this.saveLocalAssignedTests(local, local[index].candidateId || candidateId);
      return local[index];
    }
    return payload as AssignedTest;
  }

  // ==========================================
  // PRACTICAL ASSIGNMENTS API
  // ==========================================

  public normalizeAssignment(raw: any): AssessmentAssignment {
    if (!raw) {
      return {
        id: `assign-${Date.now()}`,
        title: 'Untitled Assignment',
        instructions: ''
      };
    }

    const title = raw.title || raw.name || 'Untitled Practical Assignment';
    const instructions = raw.instructions || raw.assignmentInstructions || raw.description || '';

    let starterCode = '';
    if (typeof raw.sampleAssignment === 'string') {
      starterCode = raw.sampleAssignment;
    } else if (Array.isArray(raw.sampleAssignment) && raw.sampleAssignment.length > 0) {
      const first = raw.sampleAssignment[0];
      starterCode = typeof first === 'string' ? first : (first?.code || JSON.stringify(first, null, 2));
    } else if (raw.sampleAssignment && typeof raw.sampleAssignment === 'object') {
      starterCode = raw.sampleAssignment.code || JSON.stringify(raw.sampleAssignment, null, 2);
    }

    let structured: any = {};
    if (raw.structuredData) {
      try {
        structured = typeof raw.structuredData === 'string' ? JSON.parse(raw.structuredData) : raw.structuredData;
      } catch {}
    }

    const evaluationCriteria = raw.evaluationCriteria || structured.evaluationCriteria || raw.rubric || [];
    const submissionType = raw.submissionType || structured.submissionType || 'Code';
    const allowedSubmissionTypes = raw.allowedSubmissionTypes || structured.allowedSubmissionTypes || ['Code', 'GitHubUrl', 'Photo', 'Video', 'RichText', 'FileUpload'];
    const checkerMode = (raw as any).checkerMode || structured.checkerMode || 'AI';

    return {
      ...raw,
      id: raw.id || raw._id || `assign-${Date.now()}`,
      title,
      name: raw.name || title,
      instructions,
      assignmentInstructions: raw.assignmentInstructions || instructions,
      sampleAssignment: starterCode,
      subject: raw.subject || 'Practical',
      topic: raw.topic || 'Coding',
      maxPoints: raw.maxPoints ?? raw.points ?? 100,
      points: raw.points ?? raw.maxPoints ?? 100,
      minPointsToPass: raw.minPointsToPass ?? 60,
      evaluationCriteria,
      submissionType,
      allowedSubmissionTypes,
      checkerMode
    };
  }

  public async getAssignments(courseId?: string): Promise<AssessmentAssignment[]> {
    const local = this.getLocalAssignments().map(item => this.normalizeAssignment(item));
    try {
      const filterObj: any = {};
      if (courseId) filterObj.where = { courseId };
      const filter = Object.keys(filterObj).length ? `?filter=${encodeURIComponent(JSON.stringify(filterObj))}` : '';
      const res = await fetch(`${this.baseUrl}/test-assignments${filter}`, {
        headers: this.getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          const normalized = data.map(item => this.normalizeAssignment(item));
          const seen = new Set(normalized.map(d => d.id));
          const uniqueLocal = local.filter(l => !seen.has(l.id));
          return [...uniqueLocal, ...normalized];
        }
      }
    } catch {
      // Fallback
    }
    return local;
  }

  public async getAssignmentById(id: string): Promise<AssessmentAssignment | null> {
    try {
      const res = await fetch(`${this.baseUrl}/test-assignments/${id}`, {
        headers: this.getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        return this.normalizeAssignment(data);
      }
    } catch {
      // Fallback
    }
    const found = this.getLocalAssignments().find(a => a.id === id);
    return found ? this.normalizeAssignment(found) : null;
  }

  public async createAssignment(assignment: AssessmentAssignment): Promise<AssessmentAssignment> {
    const title = assignment.title || assignment.name || 'Untitled Practical Assignment';
    const instructions = assignment.instructions || assignment.assignmentInstructions || '';
    const starter = typeof assignment.sampleAssignment === 'string' ? assignment.sampleAssignment : '';

    const auth = AuthService.getInstance();
    const payload: any = {
      name: title,
      courseId: assignment.courseId,
      level: assignment.level || 'Beginner',
      assignmentInstructions: instructions,
      subject: assignment.subject || 'Practical',
      topic: assignment.topic || 'Coding',
      points: assignment.maxPoints || assignment.points || 100,
      minPointsToPass: assignment.minPointsToPass || 60,
      submissionType: assignment.submissionType || 'Code',
      allowedSubmissionTypes: assignment.allowedSubmissionTypes || ['Code', 'GitHubUrl', 'Photo', 'Video', 'RichText', 'FileUpload'],
      userId: (assignment as any).userId || auth.getUserId() || undefined,
      companyId: (assignment as any).companyId || auth.getCompanyId() || '663dbe102a581c62fda06806'
    };

    if (starter.trim()) {
      payload.sampleAssignment = [{ code: starter }];
    }

    try {
      let res = await fetch(`${this.baseUrl}/test-assignments`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (!res.ok && res.status === 422) {
        const safePayload: any = { ...payload };
        safePayload.structuredData = JSON.stringify({
          evaluationCriteria: (assignment as any).evaluationCriteria,
          submissionType: assignment.submissionType,
          allowedSubmissionTypes: assignment.allowedSubmissionTypes,
          checkerMode: (assignment as any).checkerMode
        });
        delete safePayload.evaluationCriteria;
        delete safePayload.submissionType;
        delete safePayload.allowedSubmissionTypes;
        res = await fetch(`${this.baseUrl}/test-assignments`, {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(safePayload)
        });
      }

      if (res.ok) {
        const data = await res.json();
        const normalized = this.normalizeAssignment(data);
        const local = this.getLocalAssignments();
        local.unshift(normalized);
        this.saveLocalAssignments(local);
        return normalized;
      }
    } catch {
      // Fallback
    }

    const created: AssessmentAssignment = this.normalizeAssignment({
      ...payload,
      id: assignment.id || `assign-${Date.now()}`,
      title,
      instructions,
      sampleAssignment: starter
    });
    const local = this.getLocalAssignments();
    local.unshift(created);
    this.saveLocalAssignments(local);
    return created;
  }

  public async updateAssignment(id: string, payload: Partial<AssessmentAssignment> & { evaluationCriteria?: any[]; checkerMode?: string }): Promise<boolean> {
    try {
      const updateData: any = {};
      if (payload.title || payload.name) updateData.name = payload.title || payload.name;
      if (payload.instructions || payload.assignmentInstructions) {
        updateData.assignmentInstructions = payload.instructions || payload.assignmentInstructions;
      }
      if (payload.subject) updateData.subject = payload.subject;
      if (payload.topic) updateData.topic = payload.topic;
      if (payload.level) updateData.level = payload.level;
      if (payload.maxPoints !== undefined || payload.points !== undefined) {
        updateData.points = payload.maxPoints ?? payload.points;
      }
      if (payload.minPointsToPass !== undefined) updateData.minPointsToPass = payload.minPointsToPass;
      if (payload.isAllowSelfAssigne !== undefined) updateData.isAllowSelfAssigne = payload.isAllowSelfAssigne;
      if (payload.sampleAssignment !== undefined) {
        updateData.sampleAssignment = Array.isArray(payload.sampleAssignment)
          ? payload.sampleAssignment
          : [{ code: payload.sampleAssignment, filename: 'starter.txt' }];
      }
      // ── evaluationCriteria (array of objects as per backend model) ──
      if ((payload as any).evaluationCriteria !== undefined) {
        updateData.evaluationCriteria = (payload as any).evaluationCriteria;
      } else if (payload.rubric !== undefined) {
        updateData.evaluationCriteria = Array.isArray(payload.rubric) ? payload.rubric : [];
      }
      if ((payload as any).checkerMode !== undefined) {
        updateData.structuredData = JSON.stringify({ checkerMode: (payload as any).checkerMode });
      }
      if ((payload as any).submissionType !== undefined) {
        updateData.submissionType = (payload as any).submissionType;
      }
      if ((payload as any).allowedSubmissionTypes !== undefined) {
        updateData.allowedSubmissionTypes = (payload as any).allowedSubmissionTypes;
      }

      let res = await fetch(`${this.baseUrl}/test-assignments/${id}`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updateData)
      });

      // Resilient 422 fallback if backend returns validation error on additionalProperties
      if (!res.ok && res.status === 422) {
        const errJson = await res.clone().json().catch(() => null);
        console.warn('[AssessmentApiService] 422 validation on PATCH, packing into structuredData fallback:', errJson);

        const existingStructured = (() => {
          try {
            return typeof updateData.structuredData === 'string'
              ? JSON.parse(updateData.structuredData)
              : (updateData.structuredData || {});
          } catch {
            return {};
          }
        })();

        const fallbackStructured = {
          ...existingStructured,
          evaluationCriteria: updateData.evaluationCriteria ?? (payload as any).evaluationCriteria,
          submissionType: updateData.submissionType ?? (payload as any).submissionType,
          allowedSubmissionTypes: updateData.allowedSubmissionTypes ?? (payload as any).allowedSubmissionTypes,
          checkerMode: (payload as any).checkerMode
        };

        const safePayload: any = { ...updateData };
        delete safePayload.evaluationCriteria;
        delete safePayload.submissionType;
        delete safePayload.allowedSubmissionTypes;
        safePayload.structuredData = JSON.stringify(fallbackStructured);

        res = await fetch(`${this.baseUrl}/test-assignments/${id}`, {
          method: 'PATCH',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(safePayload)
        });
      }

      if (res.ok) {
        const local = this.getLocalAssignments();
        const idx = local.findIndex(a => (a.id === id || (a as any)._id === id));
        if (idx !== -1) {
          local[idx] = { ...local[idx], ...payload };
          this.saveLocalAssignments(local);
        }
        return true;
      }
    } catch (err) {
      console.error('[AssessmentApiService] updateAssignment error:', err);
    }
    const local = this.getLocalAssignments();
    const idx = local.findIndex(a => (a.id === id || (a as any)._id === id));
    if (idx !== -1) {
      local[idx] = { ...local[idx], ...payload };
      this.saveLocalAssignments(local);
      return true;
    }
    return false;
  }

  public async deleteAssignment(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/test-assignments/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      if (res.ok) {
        const local = this.getLocalAssignments().filter(a => a.id !== id && (a as any)._id !== id);
        this.saveLocalAssignments(local);
        return true;
      }
    } catch (err) {
      console.error('[AssessmentApiService] deleteAssignment error:', err);
    }
    // Offline fallback — remove from local store regardless
    const local = this.getLocalAssignments().filter(a => a.id !== id && (a as any)._id !== id);
    this.saveLocalAssignments(local);
    return true;
  }

  public async deleteAssignedAssignment(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/test-assigned-assignments/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      if (res.ok) {
        const local = this.getLocalAssignedAssignments().filter(a => a.id !== id);
        this.saveLocalAssignedAssignments(local);
        return true;
      }
    } catch (err) {
      console.error('[AssessmentApiService] deleteAssignedAssignment error:', err);
    }
    // Offline fallback
    const local = this.getLocalAssignedAssignments().filter(a => a.id !== id);
    this.saveLocalAssignedAssignments(local);
    return true;
  }

  public async createAssignedAssignment(assignmentId: string, assigneeId: string, extraData: any = {}): Promise<AssignedAssignment> {
    const payload: any = {
      testAssignmentId: assignmentId,
      assigneeId,
      status: 'Draft',
      created: new Date().toISOString(),
      ...extraData
    };

    try {
      const res = await fetch(`${this.baseUrl}/test-assigned-assignments`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const saved = await res.json();
        const normalized = this.normalizeAssignedAssignment(saved);
        const local = this.getLocalAssignedAssignments(assigneeId);
        local.unshift(normalized);
        this.saveLocalAssignedAssignments(local, assigneeId);
        return normalized;
      }
    } catch (err) {
      console.warn('[AssessmentApiService] createAssignedAssignment API error, falling back:', err);
    }

    const created = this.normalizeAssignedAssignment({
      id: `assign-entry-${Date.now()}`,
      ...payload
    });
    const local = this.getLocalAssignedAssignments(assigneeId);
    local.unshift(created);
    this.saveLocalAssignedAssignments(local, assigneeId);
    return created;
  }

  public async getAssignedAssignments(candidateId?: string): Promise<AssignedAssignment[]> {
    const auth = AuthService.getInstance();
    const currentUserId = auth.getUserId();
    const isPrivileged = auth.canCheckAssignments();

    // Students strictly query only their own assignments
    let targetId = candidateId;
    if (!isPrivileged) {
      targetId = currentUserId || candidateId || undefined;
    }

    if (!targetId && !isPrivileged) {
      return [];
    }

    try {
      const filterObj: any = { limit: 200 };
      if (targetId) filterObj.where = { assigneeId: targetId };
      const filter = encodeURIComponent(JSON.stringify(filterObj));
      const res = await fetch(`${this.baseUrl}/test-assigned-assignments?filter=${filter}`, {
        headers: this.getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          return data.map((s: any) => this.normalizeAssignedAssignment(s));
        }
      }
    } catch (err) {
      console.warn('[AssessmentApiService] getAssignedAssignments error, using local fallback:', err);
    }

    const local = this.getLocalAssignedAssignments(targetId);
    if (targetId) {
      return local.filter(a => a.candidateId === targetId || a.assigneeId === targetId);
    }
    return isPrivileged ? local : [];
  }

  /**
   * Submit or update an AssignedAssignment on the backend.
   *
   * Rules:
   * - Draft: saved locally only (no API call). Status stays 'Draft'.
   * - Any other status (Submitted, AIEvaluated, etc.): MUST succeed on the API.
   *   Local draft copy is updated too, but the source-of-truth is the server.
   *   If the API fails, an error is thrown — do NOT assume submission succeeded.
   */
  public async submitAssignment(submission: AssignedAssignment): Promise<AssignedAssignment> {
    const candidateId = submission.candidateId || submission.assigneeId;

    // Draft mode — save locally only, no network call
    if (submission.status === 'Draft') {
      const local = this.getLocalAssignedAssignments(candidateId);
      const index = local.findIndex(a => a.id === submission.id);
      if (index !== -1) {
        local[index] = { ...local[index], ...submission };
      } else {
        local.push(submission);
      }
      this.saveLocalAssignedAssignments(local, candidateId);
      return submission;
    }

    // For Submitted / AIEvaluated / etc. — must go to the API
    const hasId = !!submission.id && !submission.id.startsWith('sub-');
    const url = hasId
      ? `${this.baseUrl}/test-assigned-assignments/${submission.id}`
      : `${this.baseUrl}/test-assigned-assignments`;
    const method = hasId ? 'PATCH' : 'POST';

    // Map ASAE fields → backend model fields
    const payload: any = {
      testAssignmentId: submission.testAssignmentId || submission.assignmentId,
      assigneeId: submission.candidateId || submission.assigneeId,
      submittedAssignment: submission.submittedAssignment,
      status: submission.status,
      submittedDate: submission.submittedDate || new Date().toISOString(),
      aiFeedback: submission.aiFeedback,
      aiScore: submission.aiScore,
      relatedTo: submission.relatedTo,
      relatedToId: submission.relatedToId,
    };

    try {
      const res = await fetch(url, {
        method,
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const saved = await res.json();
        const normalized = this.normalizeAssignedAssignment(saved);

        const local = this.getLocalAssignedAssignments();
        const index = local.findIndex(a => a.id === normalized.id || a.id === submission.id);
        if (index !== -1) {
          local[index] = normalized;
        } else {
          local.push(normalized);
        }
        this.saveLocalAssignedAssignments(local);
        return normalized;
      }
    } catch (e) {
      console.warn('[AssessmentApiService] submitAssignment fetch error, falling back locally:', e);
    }

    // Local fallback
    const local = this.getLocalAssignedAssignments();
    const index = local.findIndex(a => a.id === submission.id);
    if (index !== -1) {
      local[index] = { ...local[index], ...submission };
    } else {
      local.push(submission);
    }
    this.saveLocalAssignedAssignments(local);
    return submission;
  }

  /**
   * Evaluates student code/practical submission via AI prompt endpoint.
   * Passes evaluationCriteria (rubric) so AI can score per-criterion.
   */
  public async evaluateAssignmentWithAI(payload: {
    instructions: string;
    sampleAssignment?: string;
    submittedAssignment: string;
    userName: string;
    evaluationCriteria?: any[];
    rubric?: any;
    maxPoints?: number;
  }): Promise<AIAssessmentFeedback> {
    try {
      const criteria = payload.evaluationCriteria || payload.rubric || [];
      const body = {
        relatedTo: 'assignment',
        appliedTo: 'evaluation',
        model: {
          ...payload,
          evaluationCriteria: JSON.stringify(criteria),
          topic: (payload as any).topic || 'Practical Assessment',
          subject: (payload as any).subject || 'Practical',
          instructions: payload.instructions,
          content: payload.submittedAssignment,
          toc: payload.instructions
        }
      };
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);
      const res = await fetch(`${this.baseUrl}/prompts/get-content`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(body),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        let data = await res.json();
        if (typeof data === 'string') {
          const cleanJson = data.replace(/^```json\s*/i, '').replace(/\s*```$/, '').trim();
          try { data = JSON.parse(cleanJson); } catch {}
        }
        if (data && typeof data === 'object') return data as AIAssessmentFeedback;
      }
    } catch (err) {
      console.warn('[AssessmentApiService] AI evaluation call failed, using fallback:', err);
    }

    // ── Heuristic fallback with per-criterion scoring ──
    const code = payload.submittedAssignment || '';
    const hasContent = code.trim().length > 30;
    const hasCodeStructures = code.includes('{') || code.includes('function') || code.includes('class') || code.includes('<');
    const criteria = payload.evaluationCriteria || payload.rubric || [];
    const maxPoints = payload.maxPoints || criteria.reduce((s: number, c: any) => s + (c.points || 0), 0) || 100;
    const baseScore = hasContent && hasCodeStructures ? 0.88 : hasContent ? 0.70 : 0.45;
    const score = Math.round(maxPoints * baseScore);

    const criteriaBreakdown = criteria.length > 0
      ? criteria.map((c: any) => ({
          criterionName: c.name || 'Criterion',
          score: Math.round((c.points || 10) * baseScore),
          maxPoints: c.points || 10,
          feedback: score >= 75 ? 'Well addressed.' : 'Needs more attention.'
        }))
      : undefined;

    return {
      score,
      maxPoints,
      percentage: Math.round(baseScore * 100),
      grade: baseScore >= 0.85 ? 'A' : baseScore >= 0.70 ? 'B' : 'C',
      isPassed: score >= (payload.maxPoints ? payload.maxPoints * 0.6 : 60),
      summary: score >= maxPoints * 0.75
        ? 'Excellent practical submission! Your work meets the core requirements and demonstrates clear understanding.'
        : 'Good starting effort. Certain requirements and edge cases need further refinement.',
      strengths: [
        'Structured and readable approach.',
        'Correctly handles the primary flow.',
        'Demonstrates understanding of the problem domain.'
      ],
      areasOfImprovement: [
        'Add more robust error handling and boundary validation.',
        'Consider adding explanatory comments for non-trivial logic.'
      ],
      codeReviewDetails: 'Automated heuristic analysis completed.',
      criteriaBreakdown
    };
  }

  /**
   * Get all submitted assignments for the checker/reviewer view.
   * Returns all pending submissions across all students.
   */
  public async getSubmissionsForChecker(filters?: {
    assignmentId?: string;
    status?: string;
  }): Promise<AssignedAssignment[]> {
    try {
      let url = `${this.baseUrl}/test-assigned-assignments?filter=${encodeURIComponent(JSON.stringify({ limit: 100 }))}`;
      const res = await this.apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        let submissions: AssignedAssignment[] = (data || []).map((s: any) => this.normalizeAssignedAssignment(s));
        if (filters?.assignmentId) {
          submissions = submissions.filter(s => s.assignmentId === filters.assignmentId || s.testAssignmentId === filters.assignmentId);
        }
        if (filters?.status) {
          submissions = submissions.filter(s => s.status === filters.status);
        }
        return submissions;
      }
    } catch (err) {
      console.warn('[AssessmentApiService] getSubmissionsForChecker error:', err);
    }
    // Fallback: return all local assigned assignments
    let local = this.getLocalAssignedAssignments();
    if (filters?.assignmentId) local = local.filter(s => s.assignmentId === filters.assignmentId);
    if (filters?.status) local = local.filter(s => s.status === filters.status);
    return local;
  }

  /**
   * Save human reviewer feedback for a submission.
   */
  public async saveHumanFeedback(submissionId: string, humanFeedback: import('../types/Assignment').HumanFeedback): Promise<boolean> {
    const status = humanFeedback.status === 'Approved' ? 'Approved'
      : humanFeedback.status === 'Rejected' ? 'Rejected'
      : humanFeedback.status === 'NeedsRevision' ? 'NeedsRevision' : 'UnderReview';

    const payload = {
      humanFeedback,
      humanScore: humanFeedback.totalScore,
      status,
      reviewDate: new Date().toISOString()
    };

    const isClientOnlyId = !submissionId || submissionId.startsWith('sub-') || submissionId.startsWith('assign-entry-');

    if (!isClientOnlyId) {
      try {
        const res = await this.apiFetch(`${this.baseUrl}/test-assigned-assignments/${submissionId}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });

        if (res.ok) {
          const local = this.getLocalAssignedAssignments();
          const idx = local.findIndex(s => s.id === submissionId);
          if (idx !== -1) {
            local[idx] = { ...local[idx], humanFeedback, humanScore: humanFeedback.totalScore, status: status as any };
            this.saveLocalAssignedAssignments(local);
          }
          return true;
        }

        if (res.status === 404) {
          console.warn('[AssessmentApiService] 404 on PATCH saveHumanFeedback, attempting POST fallback:', submissionId);
        }
      } catch (err) {
        console.warn('[AssessmentApiService] PATCH saveHumanFeedback network error, trying fallback:', err);
      }
    }

    // Fallback: POST new record if client-only ID or 404 on PATCH
    try {
      const targetSub = this.getLocalAssignedAssignments().find(s => s.id === submissionId);
      const auth = AuthService.getInstance();
      const postPayload: any = {
        testAssignmentId: targetSub?.testAssignmentId || targetSub?.assignmentId || undefined,
        assigneeId: targetSub?.assigneeId || targetSub?.candidateId || auth.getUserId() || undefined,
        ...payload
      };
      const res = await this.apiFetch(`${this.baseUrl}/test-assigned-assignments`, {
        method: 'POST',
        body: JSON.stringify(postPayload)
      });
      if (res.ok) {
        const saved = await res.json();
        const normalized = this.normalizeAssignedAssignment(saved);
        const local = this.getLocalAssignedAssignments();
        const idx = local.findIndex(s => s.id === submissionId);
        if (idx !== -1) {
          local[idx] = normalized;
        } else {
          local.unshift(normalized);
        }
        this.saveLocalAssignedAssignments(local);
        return true;
      }
    } catch (err) {
      console.warn('[AssessmentApiService] POST fallback saveHumanFeedback error:', err);
    }

    // Final local fallback for UI reactivity
    const local = this.getLocalAssignedAssignments();
    const idx = local.findIndex(s => s.id === submissionId);
    if (idx !== -1) {
      local[idx] = { ...local[idx], humanFeedback, humanScore: humanFeedback.totalScore, status: status as any };
      this.saveLocalAssignedAssignments(local);
      return true;
    }
    return false;
  }

  private normalizeAssignedAssignment(raw: any): AssignedAssignment {
    // submittedAssignment: preserve as SubmittedFile[] if it is an array of objects
    let submittedAssignment: any = raw.submittedAssignment;
    if (Array.isArray(submittedAssignment) && submittedAssignment.length > 0) {
      // If already objects with 'type' field, keep as-is (SubmittedFile[])
      if (typeof submittedAssignment[0] === 'object' && submittedAssignment[0] !== null) {
        // keep as array
      } else {
        // Legacy format — was stringified
        try { submittedAssignment = JSON.parse(submittedAssignment[0]); } catch {}
      }
    }

    // Map any legacy snake_case status to PascalCase
    const rawStatus = raw.status || 'Draft';
    const statusMap: Record<string, AssignedAssignment['status']> = {
      'Draft': 'Draft',
      'Submitted': 'Submitted',
      'AI_Evaluated': 'AIEvaluated',
      'AIEvaluated': 'AIEvaluated',
      'Under_Review': 'UnderReview',
      'UnderReview': 'UnderReview',
      'Approved': 'Approved',
      'Rejected': 'Rejected',
      'Needs_Revision': 'NeedsRevision',
      'NeedsRevision': 'NeedsRevision',
    };

    return {
      id: raw.id || raw._id,
      assignmentId: raw.testAssignmentId || raw.assignmentId || '',
      testAssignmentId: raw.testAssignmentId,
      candidateId: raw.assigneeId || raw.candidateId || raw.userId || '',
      assigneeId: raw.assigneeId,
      userName: raw.userName || '',
      submittedAssignment,
      submittedDate: raw.submittedDate || raw.modified || raw.created,
      status: statusMap[rawStatus] ?? 'Draft',
      aiFeedback: raw.aiFeedback,
      humanFeedback: raw.humanFeedback,
      aiScore: raw.aiScore ?? raw.aiFeedback?.score,
      humanScore: raw.humanScore ?? raw.humanFeedback?.totalScore,
      reviewerId: raw.reviewerId,
      reviewDate: raw.reviewDate,
      relatedTo: raw.relatedTo,
      relatedToId: raw.relatedToId
    };
  }

  // ==========================================
  // LOCAL CACHE & MOCK SEEDING
  // ==========================================

  private getLocalTests(): AssessmentTest[] {
    try {
      const data = localStorage.getItem('as_tests');
      if (data) {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    const defaultTest = this.getDefaultSeedTest();
    this.saveLocalTests([defaultTest]);
    return [defaultTest];
  }

  private safeSetItem(key: string, items: any[]): void {
    try {
      localStorage.setItem(key, JSON.stringify(items));
    } catch (e) {
      console.warn(`Storage quota exceeded for "${key}", truncating to latest items:`, e);
      try {
        localStorage.setItem(key, JSON.stringify(items.slice(0, 15)));
      } catch {
        try {
          localStorage.setItem(key, JSON.stringify(items.slice(0, 5)));
        } catch {
          // Ignore if completely full
        }
      }
    }
  }

  private saveLocalTests(tests: AssessmentTest[]): void {
    this.safeSetItem('as_tests', tests);
  }

  private getLocalQuestions(): AssessmentQuestion[] {
    try {
      const data = localStorage.getItem('as_questions');
      if (data) {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    const defaultQuestions = this.getDefaultSeedQuestions();
    this.saveLocalQuestions(defaultQuestions);
    return defaultQuestions;
  }

  private saveLocalQuestions(questions: AssessmentQuestion[]): void {
    this.safeSetItem('as_questions', questions);
  }

  private getAssignedTestsCacheKey(candidateId?: string): string {
    const id = candidateId || AuthService.getInstance().getUserId();
    return id ? `as_assigned_tests_${id}` : 'as_assigned_tests';
  }

  private getLocalAssignedTests(candidateId?: string): AssignedTest[] {
    try {
      const key = this.getAssignedTestsCacheKey(candidateId);
      const data = localStorage.getItem(key);
      if (data) return JSON.parse(data);
      // Backward compatibility: read legacy un-scoped key
      const legacyData = localStorage.getItem('as_assigned_tests');
      if (legacyData) {
        const parsed: AssignedTest[] = JSON.parse(legacyData);
        if (candidateId) {
          return parsed.filter(t => t.candidateId === candidateId);
        }
        return parsed;
      }
    } catch {}
    return [];
  }

  private saveLocalAssignedTests(items: AssignedTest[], candidateId?: string): void {
    const key = this.getAssignedTestsCacheKey(candidateId);
    this.safeSetItem(key, items);
  }

  private getLocalAssignments(): AssessmentAssignment[] {
    try {
      const data = localStorage.getItem('as_assignments');
      if (data) {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    const defaultAssignment = this.getDefaultSeedAssignment();
    this.saveLocalAssignments([defaultAssignment]);
    return [defaultAssignment];
  }

  private saveLocalAssignments(items: AssessmentAssignment[]): void {
    this.safeSetItem('as_assignments', items);
  }

  private getAssignedAssignmentsCacheKey(candidateId?: string): string {
    const id = candidateId || AuthService.getInstance().getUserId();
    return id ? `as_assigned_assignments_${id}` : 'as_assigned_assignments';
  }

  private getLocalAssignedAssignments(candidateId?: string): AssignedAssignment[] {
    try {
      const key = this.getAssignedAssignmentsCacheKey(candidateId);
      const data = localStorage.getItem(key);
      if (data) return JSON.parse(data);
      // Backward compatibility: read legacy un-scoped key
      const legacyData = localStorage.getItem('as_assigned_assignments');
      if (legacyData) {
        const parsed: AssignedAssignment[] = JSON.parse(legacyData);
        if (candidateId) {
          return parsed.filter(a => a.candidateId === candidateId || a.assigneeId === candidateId);
        }
        return parsed;
      }
    } catch {}
    return [];
  }

  private saveLocalAssignedAssignments(items: AssignedAssignment[], candidateId?: string): void {
    const key = this.getAssignedAssignmentsCacheKey(candidateId);
    this.safeSetItem(key, items);
  }

  private initDefaultMockData(): void {
    this.getLocalQuestions();
    this.getLocalTests();
    this.getLocalAssignments();
  }

  private getDefaultSeedQuestions(): AssessmentQuestion[] {
    return [
      {
        id: 'q-demo-1',
        question: 'Identify which of the following geometric angles represents a right angle (90 degrees):',
        description: 'Hint: A right angle forms an exact perpendicular corner L-shape.',
        image: 'https://images.unsplash.com/photo-1509228468518-180dd4864904?w=600&auto=format&fit=crop&q=80',
        type: 'MultipleChoice',
        category: 'Theory',
        subject: 'Mathematics',
        topic: 'Geometry',
        complexity: 1,
        correctPoints: 1,
        incorrectPoints: 0,
        options: [
          { value: 'Acute Angle (45°)', image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=200&auto=format&fit=crop&q=80' },
          { value: 'Right Angle (90°)', image: 'https://images.unsplash.com/photo-1596495578065-6e0763fa1178?w=200&auto=format&fit=crop&q=80' },
          { value: 'Obtuse Angle (135°)', image: 'https://images.unsplash.com/photo-1518133910546-b6c2fb7d79e3?w=200&auto=format&fit=crop&q=80' },
          { value: 'Straight Angle (180°)' }
        ],
        answer: { option: 1, value: 'Right Angle (90°)' },
        explanation: 'A right angle measures precisely 90 degrees and forms a perpendicular intersection.',
        feedback: {
          correct: 'Spot on! A right angle is always 90 degrees.',
          incorrect: 'Not quite. Recall that a perpendicular corner forms a 90° right angle.'
        }
      },
      {
        id: 'q-demo-2',
        question: 'Which of the following elements are HTML5 semantic block-level container tags?',
        description: 'Select all correct semantic containers.',
        type: 'MultipleResponse',
        category: 'Theory',
        subject: 'Web Development',
        topic: 'HTML5',
        complexity: 2,
        correctPoints: 2,
        incorrectPoints: -0.5,
        options: [
          { value: '<article>' },
          { value: '<span>' },
          { value: '<section>' },
          { value: '<code>' }
        ],
        answer: { option: [0, 2], value: ['<article>', '<section>'] },
        explanation: '<article> and <section> are semantic block elements. <span> and <code> are inline elements.',
        feedback: {
          correct: 'Great work! Both <article> and <section> are semantic block elements.',
          incorrect: 'Review block vs inline semantic elements.'
        }
      },
      {
        id: 'q-demo-3',
        question: 'Match each SVG element with its correct graphical geometry role:',
        description: 'Connect tags on the left with their geometric shape descriptions on the right.',
        type: 'Matching',
        category: 'Practical',
        subject: 'Graphics',
        topic: 'SVG',
        complexity: 2,
        correctPoints: 3,
        options: [
          { value: '<circle>' },
          { value: '<rect>' },
          { value: '<path>' },
          { value: '<polygon>' }
        ],
        matchingOptions: [
          { value: 'Defined by cx, cy, and radius r' },
          { value: 'Defined by x, y, width, and height' },
          { value: 'Complex curves defined by SVG d attribute' },
          { value: 'Closed multi-point planar polygon' }
        ],
        answer: {
          option: [
            { option: 0, matchingOption: 0 },
            { option: 1, matchingOption: 1 },
            { option: 2, matchingOption: 2 },
            { option: 3, matchingOption: 3 }
          ]
        },
        explanation: 'SVG uses dedicated element tags with geometry attributes for circles, rectangles, paths, and polygons.'
      },
      {
        id: 'q-demo-4',
        question: 'In animation physics, Newton\'s first law states that an object remains at rest or in uniform motion unless acted upon by an external <________>.',
        description: 'Fill in the blank term.',
        type: 'FillInBlanks',
        category: 'Theory',
        subject: 'Physics',
        topic: 'Mechanics',
        complexity: 1,
        correctPoints: 1,
        answer: { value: ['force'] },
        explanation: 'Newton\'s first law of motion describes inertia: a net external force is required to change velocity.'
      },
      {
        id: 'q-demo-5',
        question: 'Briefly explain the role of keyframes in digital computer animation.',
        description: 'Provide a concise technical definition mentioning start/end poses and interpolation.',
        type: 'ShortAnswer',
        category: 'Theory',
        subject: 'Animation',
        topic: 'Keyframing',
        complexity: 2,
        correctPoints: 2,
        answer: {
          value: 'Keyframes define the crucial start and end positions of an animated sequence, allowing in-between frames to be smoothly interpolated by the engine.',
          points: ['start and end positions', 'in-between frames', 'interpolation']
        },
        explanation: 'Keyframes mark essential anchor states along the timeline between which animation engines calculate tweening.'
      }
    ];
  }

  private getDefaultSeedTest(): AssessmentTest {
    const seedQuestions = this.getDefaultSeedQuestions();
    return {
      id: 'test-comprehensive-demo',
      name: 'ASAE Master Diagnostic & Creative Skills Assessment',
      language: 'en',
      category: 'Theory & Practical',
      testType: 'Topic',
      subject: 'Interactive Media & Web Tech',
      topic: 'Core Foundations',
      questionsCount: 5,
      questions: seedQuestions.map(q => q.id!),
      Questions: seedQuestions,
      points: 9,
      minPointsToPass: 6,
      retakeCount: 3,
      duration: 15,
      hintMinusPoint: 0.25,
      config: {
        questionAttempt: true,
        endtest: true,
        sequence: true,
        showResults: true,
        showFullResult: true,
        enableFullScreen: false,
        enableIp: false,
        retake: true,
        retakeCount: 3,
        maxAllowUserEvents: 5,
        trackEvent: {
          copy: true,
          paste: true,
          contextmenu: true,
          visibilitychange: true,
          showCameraPreview: false,
          faceMovement: false,
          textToVoice: true,
          eventFeedback: true
        },
        instruction: `<h3>Welcome to the Master Diagnostic Assessment!</h3>
        <p>Please read these guidelines before beginning:</p>
        <ul>
          <li><strong>Total Questions:</strong> 5 questions covering various question types (Multiple Choice, Multi-select, Matching, Fill-in-blanks, Short Answer).</li>
          <li><strong>Time Limit:</strong> 15 minutes with real-time countdown timer.</li>
          <li><strong>Passing Score:</strong> 6 out of 9 points.</li>
          <li><strong>Proctoring:</strong> Tab switching, copying, and right-clicking are tracked.</li>
          <li><strong>Audio Feedback:</strong> Voice results summary and audio chimes will play upon completion.</li>
        </ul>`
      }
    };
  }

  private getDefaultSeedAssignment(): AssessmentAssignment {
    return {
      id: 'assign-svg-canvas',
      title: 'Practical Coding: Responsive SVG Card Component',
      subject: 'Graphics',
      topic: 'SVG & Canvas',
      instructions: `### Assignment Objective
Create a semantic, responsive SVG component that renders:
1. An outer container with \`viewBox="0 0 800 400"\`
2. A subtle gradient background
3. A centered decorative geometry icon (\`<circle>\` or \`<polygon>\`)
4. Styled text with \`fill="#f8fafc"\`

Submit your code below in the editor. When submitted, our AI Review Engine will inspect your syntax, responsiveness, and aesthetic polish!`,
      sampleAssignment: `<svg viewBox="0 0 800 400" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="cardGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#1e1b4b" />
      <stop offset="100%" stop-color="#0f172a" />
    </linearGradient>
  </defs>

  <!-- Background Card -->
  <rect width="800" height="400" rx="20" fill="url(#cardGrad)" stroke="#6366f1" stroke-width="2" />

  <!-- Center Decorative Shape -->
  <circle cx="400" cy="170" r="60" fill="#4f46e5" opacity="0.8" />

  <!-- Title Text -->
  <text x="400" y="280" text-anchor="middle" fill="#f8fafc" font-size="24" font-weight="bold" font-family="Inter, sans-serif">
    Hello ApnaGuru
  </text>
</svg>`,
      maxPoints: 100,
      minPointsToPass: 60
    };
  }

  private generateFallbackQuestions(payload: any): AssessmentQuestion[] {
    const list: AssessmentQuestion[] = [];
    const count = payload.noOfQuestions || 3;
    for (let i = 1; i <= count; i++) {
      list.push({
        id: `gen-q-${Date.now()}-${i}`,
        question: `Sample Question ${i} on ${payload.topic || 'Subject'}: What is the primary characteristic of this concept?`,
        description: 'Hint: Focus on the core foundational definition.',
        type: (payload.type as any) || 'MultipleChoice',
        category: 'Theory',
        subject: payload.subject || 'General',
        topic: payload.topic || 'Topic',
        complexity: payload.complexity || 2,
        correctPoints: 1,
        options: [
          { value: `Key foundational principle of ${payload.topic || 'the subject'}` },
          { value: 'An arbitrary secondary characteristic' },
          { value: 'Outdated legacy terminology' },
          { value: 'Unrelated external concept' }
        ],
        answer: { option: 0, value: `Key foundational principle of ${payload.topic || 'the subject'}` },
        explanation: `This question evaluates the primary principle governing ${payload.topic || 'the subject'}.`,
        feedback: {
          correct: 'Excellent job! You grasped the foundational concept.',
          incorrect: 'Review the subject definition.'
        }
      });
    }
    return list;
  }

  // ==========================================
  // COURSE PROGRESS API (POST /api/course-progresses)
  // ==========================================

  public async recordCourseProgress(payload: {
    candidateId: string;
    courseId: string;
    courseContentType: 'test' | 'quiz' | 'assignment' | 'topic';
    courseContentStatus: 'Done' | 'Completed' | 'Pending';
    courseContentDetails?: any;
    obtainedPoints?: number;
    totalPoints?: number;
  }): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/course-progresses`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      return res.ok;
    } catch (err) {
      console.warn('[AssessmentApiService] Fallback local course progress recorded:', payload);
      const local = JSON.parse(localStorage.getItem('asae_course_progresses') || '[]');
      local.push({ ...payload, timestamp: new Date().toISOString() });
      localStorage.setItem('asae_course_progresses', JSON.stringify(local));
      return true;
    }
  }

  public async getCourseProgress(courseId: string, candidateId: string): Promise<any[]> {
    try {
      const filter = encodeURIComponent(JSON.stringify({ where: { courseId, candidateId } }));
      const res = await fetch(`${this.baseUrl}/course-progresses?filter=${filter}`, {
        headers: this.getAuthHeaders()
      });
      if (res.ok) return await res.json();
    } catch {}
    const local = JSON.parse(localStorage.getItem('asae_course_progresses') || '[]');
    return local.filter((item: any) => item.courseId === courseId && item.candidateId === candidateId);
  }

  public async uploadFile(file: File | Blob, folder: string = 'assignment-submissions', fileName?: string): Promise<string> {
    const finalFileName = fileName || (file instanceof File ? file.name : `file_${Date.now()}`);
    const formData = new FormData();
    formData.append('file', file, finalFileName);

    try {
      const authHeaders = AuthService.getInstance().getAuthHeaders();
      const res = await fetch(`${this.baseUrl}/buckets/${encodeURIComponent(folder)}/upload`, {
        method: 'POST',
        headers: {
          ...authHeaders
        },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data[0]) {
          return data[0].url || data[0].Location || data[0].path || data[0].key || '';
        }
        return data.url || data.Location || data.path || data.key || '';
      }
    } catch (err) {
      console.warn('[AssessmentApiService] uploadFile network error, fallback to local object URL:', err);
    }

    return URL.createObjectURL(file);
  }
}


